import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { IKanbanTask } from '@/types/kanban';

export interface TaskNodeData {
  task: IKanbanTask;
  onEdit: (task: IKanbanTask) => void;
}

const statusColors: Record<string, { bg: string; border: string; text: string }> = {
  Plan: {
    bg: 'bg-gray-100 dark:bg-gray-700',
    border: 'border-gray-400 dark:border-gray-500',
    text: 'text-gray-600 dark:text-gray-400',
  },
  Doing: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    border: 'border-blue-400 dark:border-blue-500',
    text: 'text-blue-600 dark:text-blue-400',
  },
  Review: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    border: 'border-yellow-400 dark:border-yellow-500',
    text: 'text-yellow-600 dark:text-yellow-400',
  },
  Done: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    border: 'border-green-400 dark:border-green-500',
    text: 'text-green-600 dark:text-green-400',
  },
};

interface TaskNodeProps {
  data: TaskNodeData;
}

const TaskNode = memo(({ data }: TaskNodeProps) => {
  const { task, onEdit } = data;
  const colors = statusColors[task.status] || statusColors.Plan;

  return (
    <div
      onClick={() => onEdit(task)}
      className={`px-4 py-3 rounded-lg border-2 shadow-md cursor-pointer min-w-[180px] max-w-[220px] transition-all hover:shadow-lg ${colors.bg} ${colors.border}`}
    >
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 !bg-gray-400 dark:!bg-gray-500"
      />

      {/* Status Badge */}
      <div className="flex items-center justify-between mb-2">
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}
        >
          {task.status}
        </span>
        {task.duration > 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {task.duration}h
          </span>
        )}
      </div>

      {/* Task Name */}
      <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-2 line-clamp-2">
        {task.task_name}
      </h4>

      {/* Owner */}
      <div className="flex items-center gap-1.5">
        <div className="w-5 h-5 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
          <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
            {task.task_owner.charAt(0).toUpperCase()}
          </span>
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {task.task_owner}
        </span>
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 !bg-gray-400 dark:!bg-gray-500"
      />
    </div>
  );
});

TaskNode.displayName = 'TaskNode';

export default TaskNode;
